
import { Vue, Options, prop } from "vue-class-component";
import moment from "moment";
import { cloneDeep } from "lodash";
import { Cell, Panel, PanelDate, Range, Side } from "./type";

class Props {
  modelValue = prop<Range>({ type: Object, default: () => new Range() });
  defaultPreset = prop({
    type: Number,
    default: 0,
    validator: (v: number) => [0, 7, 14, 30].includes(v)
  });
  minDate = prop<Date | null>({ type: Date, default: null });
  maxDate = prop<Date | null>({ type: Date, default: null });
  maxRange = prop({ type: Number, default: 0 });
  placeholder = prop({ type: String, default: "" });
}

@Options({
  name: "DateRangePicker",
  emits: ["update:modelValue", "apply", "preset", "reset", "select-date"]
})
export default class DateRangePicker extends Vue.with(Props) {
  get weekdays() {
    return [
      this.$t("pickupDashboard.dateRangePicker.weekdays.mon"),
      this.$t("pickupDashboard.dateRangePicker.weekdays.tue"),
      this.$t("pickupDashboard.dateRangePicker.weekdays.wed"),
      this.$t("pickupDashboard.dateRangePicker.weekdays.thu"),
      this.$t("pickupDashboard.dateRangePicker.weekdays.fri"),
      this.$t("pickupDashboard.dateRangePicker.weekdays.sat"),
      this.$t("pickupDashboard.dateRangePicker.weekdays.sun")
    ];
  }
  today = moment().startOf("day");

  get placeholderLabel(): string {
    return this.placeholder || this.$t("pickupDashboard.dateRangePicker.allTime");
  }

  get presets() {
    return [
      { label: "pickupDashboard.dateRangePicker.customRange", days: 0, id: "custom_range" },
      { label: "pickupDashboard.dateRangePicker.last7Days", days: 7, id: "last_7_days" },
      { label: "pickupDashboard.dateRangePicker.last14Days", days: 14, id: "last_14_days" },
      { label: "pickupDashboard.dateRangePicker.last30Days", days: 30, id: "last_30_days" }
    ];
  }
  selectedPreset = 0;
  disabledPreset(days: number): boolean {
    // if (!days) return false;

    // const noInRange = this.maxRange && this.maxRange < days;

    // const passDays =
    //   this.maxDate && moment(this.maxDate)?.isBefore(this.today, "day");

    // return Boolean(noInRange || passDays);

    // if want validate the props, use above
    return false;
  }

  // single selected range state
  range = new Range();

  // hover moment used for cross-panel preview
  hover: moment.Moment | null = null;

  // panels (two months view)
  panel = new Panel();

  openOverlayPanel(event?: Event) {
    (this.$refs as any)?.opDateRangePicker?.toggle(event);
  }
  closeOverlayPanel(event?: Event) {
    (this.$refs as any)?.opDateRangePicker?.hide(event);
  }

  private mDay(cell: Cell) {
    return moment([cell.year, cell.month, cell.day]).startOf("day");
  }
  private toStart(d?: Date | moment.Moment | null) {
    return (d ? moment(d) : moment()).startOf("day");
  }
  private toLocalDate(m: moment.Moment) {
    return moment
      .utc([m.year(), m.month(), m.date()])
      .startOf("day")
      .toDate();
  }

  computePanelFromDate(m: moment.Moment): PanelDate {
    const month = m.month(),
      year = m.year(),
      daysInMonth = m.daysInMonth();
    const firstDay = (moment([year, month, 1]).day() + 6) % 7;
    const total = Math.ceil((firstDay + daysInMonth) / 7) * 7;
    return {
      month,
      year,
      daysInMonth,
      firstDay,
      trailing: total - (firstDay + daysInMonth)
    };
  }

  syncPanelsFromPrimaryMoment(m: moment.Moment) {
    this.panel.primary = this.computePanelFromDate(m.clone());
    this.panel.secondary = this.computePanelFromDate(m.clone().add(1, "month"));
  }

  prevMonth(side: Side) {
    if (side === "primary") {
      const base = moment([
        this.panel.primary.year,
        this.panel.primary.month,
        1
      ]).subtract(1, "month");
      this.syncPanelsFromPrimaryMoment(base);
    } else {
      const base = moment([
        this.panel.secondary.year,
        this.panel.secondary.month,
        1
      ]).subtract(1, "month");
      this.panel.secondary = this.computePanelFromDate(base);
      this.panel.primary = this.computePanelFromDate(
        base.clone().subtract(1, "month")
      );
    }
  }

  nextMonth(side: Side) {
    if (side === "primary") {
      const base = moment([
        this.panel.primary.year,
        this.panel.primary.month,
        1
      ]).add(1, "month");
      this.syncPanelsFromPrimaryMoment(base);
    } else {
      const base = moment([
        this.panel.secondary.year,
        this.panel.secondary.month,
        1
      ]).add(1, "month");
      this.panel.secondary = this.computePanelFromDate(base);
      this.panel.primary = this.computePanelFromDate(
        base.clone().subtract(1, "month")
      );
    }
  }

  monthYearLabel(p: PanelDate) {
    return moment([p.year, p.month, 1]).format("MMMM YYYY");
  }
  formatDate(d: Date | null, fmt = "D MMM YYYY") {
    return d ? moment(d).format(fmt) : "";
  }
  formatRange(s: Date | null, e: Date | null) {
    if (!s || !e) return "";

    return `${this.formatDate(s)} - ${this.formatDate(e)}`;
  }

  getCalendarGrid(p: PanelDate): Cell[] {
    const year = p.year,
      month = p.month,
      firstDay = p.firstDay,
      daysInMonth = p.daysInMonth;
    const prevMonth = moment([year, month, 1]).subtract(1, "month");
    const prevDays = prevMonth.daysInMonth();
    const prev: Cell[] = Array.from({ length: firstDay }, (_, i) => ({
      day: prevDays - firstDay + 1 + i,
      type: "prev",
      year: prevMonth.year(),
      month: prevMonth.month()
    }));
    const curr: Cell[] = Array.from({ length: daysInMonth }, (_, i) => ({
      day: i + 1,
      type: "current",
      year,
      month
    }));
    const total = prev.length + curr.length;
    const nextLength = 42 - total;
    const nextMonth = moment([year, month, 1]).add(1, "month");
    const next: Cell[] = Array.from({ length: nextLength }, (_, i) => ({
      day: i + 1,
      type: "next",
      year: nextMonth.year(),
      month: nextMonth.month()
    }));
    return [...prev, ...curr, ...next];
  }

  /* Selection helpers using single range */
  isSelectedDay(cell: Cell) {
    if (!this.range.start) return false;
    const day = this.mDay(cell);
    const s = this.toStart(this.range.start);
    const e = this.range.end ? this.toStart(this.range.end) : null;
    return day.isSame(s, "day") || (e && day.isSame(e, "day"));
  }

  isDisabledDay(cell: Cell) {
    const day = this.mDay(cell);

    // ---- RULE 1: MIN / MAX props ----
    if (this.minDate && day.isBefore(this.minSelectedDate, "day")) return true;
    if (this.maxDate && day.isAfter(this.maxSelectedDate, "day")) return true;

    // ---- RULE 2: Range belum lengkap (preview) ----
    if (this.range.start && !this.range.end && this.maxRange) {
      const start = this.toStart(this.range.start);

      // CASE: preset = Tentukan tanggal (custom range)
      if (!this.selectedPreset) {
        let minLimit = start.clone().subtract(this.maxRange - 1, "days");
        let maxLimit = start.clone().add(this.maxRange - 1, "days");

        // min & max props priority for minLimit & maxLimit
        if (this.minSelectedDate?.isAfter(minLimit))
          minLimit = this.minSelectedDate.clone();

        if (this.maxSelectedDate?.isBefore(maxLimit))
          maxLimit = this.maxSelectedDate.clone();

        return day.isBefore(minLimit, "day") || day.isAfter(maxLimit, "day");
      }

      // CASE: preset selain custom, range fixed by preset → disable semua
      return true;
    }

    if (cell.type !== "current") return true;

    return false;
  }

  isInPreviewRange(cell: Cell) {
    const day = this.mDay(cell);

    // CASE 1: belum ada start → tidak ada range
    if (!this.range.start) return false;

    // CASE 2: start ada tapi end belum ada (preview)
    if (!this.range.end && this.hover) {
      const s = this.toStart(this.range.start);
      const h = this.toStart(this.hover);
      const min = s.isBefore(h) ? s : h;
      const max = s.isBefore(h) ? h : s;

      return day.isBetween(min, max, undefined, "[]");
    }

    // CASE 3: start + end sudah lengkap (final)
    if (this.range.start && this.range.end) {
      const s = this.toStart(this.range.start);
      const e = this.toStart(this.range.end);
      const min = s.isBefore(e) ? s : e;
      const max = s.isBefore(e) ? e : s;

      return day.isBetween(min, max, undefined, "[]");
    }

    return false;
  }

  isPreviewStart(cell: Cell) {
    const day = this.mDay(cell);

    // Preview mode
    if (this.range.start && !this.range.end && this.hover) {
      const s = this.toStart(this.range.start);
      const h = this.toStart(this.hover);
      const min = s.isBefore(h) ? s : h;
      return day.isSame(min, "day");
    }

    // Final mode
    if (this.range.start && this.range.end) {
      return day.isSame(this.toStart(this.range.start));
    }

    return false;
  }

  isPreviewEnd(cell: Cell) {
    const day = this.mDay(cell);

    // Preview mode
    if (this.range.start && !this.range.end && this.hover) {
      const s = this.toStart(this.range.start);
      const h = this.toStart(this.hover);
      const max = s.isBefore(h) ? h : s;
      return day.isSame(max, "day");
    }

    // Final mode
    if (this.range.end) {
      return day.isSame(this.toStart(this.range.end));
    }

    return false;
  }

  isToday(cell: Cell) {
    const day = this.mDay(cell);
    return day.isSame(this.today, "day");
  }

  cellClass(cell: Cell) {
    const isCurrent = cell.type === "current";
    const selected = this.isSelectedDay(cell);
    const inRange = this.isInPreviewRange(cell);
    const disabled = this.isDisabledDay(cell);
    return {
      "text-black-lp-200 text-opacity-25": !isCurrent,
      "cursor-not-allowed": !isCurrent,
      "font-medium text-white bg-red-lp-100": selected && isCurrent,
      "group-hover:font-medium group-hover:text-white group-hover:bg-red-lp-100":
        isCurrent && !disabled,
      "opacity-50 cursor-not-allowed": disabled,
      "text-gray-lp-800": inRange && isCurrent && !selected
    };
  }

  /* Mouse handlers (single hover state) */
  handleMouseLeave(_side: Side) {
    this.hover = null;
  }

  onCellEnter(cell: Cell) {
    if (this.isDisabledDay(cell)) return;
    this.hover = this.mDay(cell);
  }
  onCellLeave() {
    this.hover = null;
  }

  selectDay(cell: Cell) {
    const clicked = this.mDay(cell);

    // reset presets when user interacts
    if (this.selectedPreset) this.selectedPreset = 0;

    // 1) No start yet OR already complete -> set start
    if (!this.range.start || this.range.end) {
      this.range.start = this.toLocalDate(clicked);
      this.range.end = null;
      return;
    }

    // 2) Have start, no end -> set end (with swap if clicked before start)
    const startMoment = this.toStart(this.range.start);
    if (clicked.isBefore(startMoment)) {
      // swap
      this.range.end = this.range.start;
      this.range.start = this.toLocalDate(clicked);
    } else {
      this.range.end = this.toLocalDate(clicked);
    }

    this.selectedPreset = this.detectPresetFromPrimaryDate(this.range);
  }

  /* Presets / apply / reset */
  get minSelectedDate() {
    return this.minDate ? this.toStart(this.minDate) : null;
  }

  get maxSelectedDate() {
    return this.maxDate ? this.toStart(this.maxDate) : null;
  }

  applyPreset(days: number) {
    this.selectedPreset = this.disabledPreset(days) ? 0 : days;

    if (!days || this.disabledPreset(days)) {
      this.range = new Range();
      this.syncPanelsFromPrimaryMoment(moment());
      return;
    }

    const end =
      this.maxSelectedDate && this.maxSelectedDate.isBefore(this.today, "day")
        ? this.maxSelectedDate.clone()
        : this.today.clone();

    let start = end.clone().subtract(days - 1, "days");

    if (this.minSelectedDate && start.isBefore(this.minSelectedDate, "day")) {
      start = this.minSelectedDate.clone();
    }

    this.range = new Range({
      start: this.toLocalDate(start),
      end: this.toLocalDate(end)
    });

    // panels: primary shows start
    this.syncPanelsFromPrimaryMoment(start);
    this.$emit("preset", days);
  }

  onReset() {
    this.applyPreset(this.defaultPreset);
    this.$emit("update:modelValue", this.range);
    this.$emit("reset");
  }

  get disabledApply() {
    return !(this.range.start && this.range.end);
  }

  apply(event?: Event) {
    this.$emit("update:modelValue", this.range);
    this.$emit("apply", this.range);

    this.closeOverlayPanel(event);
  }

  detectPresetFromPrimaryDate(range: Range): number {
    if (!range?.start || !range?.end) return 0;

    const start = moment(range.start).startOf("day");
    const end = moment(range.end).startOf("day");

    // diff inclusive
    const total = end.diff(start, "days") + 1;

    // must match one of the presets
    const presets = [7, 14, 30];
    if (!presets.includes(total)) return 0;

    // check "end must be today"
    if (!end.isSame(this.today)) return 0;

    // success
    return total;
  }

  isOpen = false;
  mounted() {
    this.applyPreset(this.defaultPreset);

    const op: any = (this.$refs as any)?.opDateRangePicker;
    if (op) {
      op.$watch("visible", (val: boolean) => {
        this.isOpen = val;

        if (!val) {
          this.$nextTick(() => {
            this.range = cloneDeep(new Range(this.modelValue));

            this.selectedPreset = this.detectPresetFromPrimaryDate(this.range);

            this.syncPanelsFromPrimaryMoment(
              this.range.start ? moment(this.range.start) : moment()
            );
          });
        }
      });
    }
  }
}
